<script setup>
import { ref, computed, watch, onMounted, reactive } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { WORK_APR_ST } from '@/constants/WORK_APR_ST'
import { formatarDataBR } from '@/utils/useDataBR'
import WorkflowAprovacaoModal from './WorkflowAprovacaoModal.vue'

const props = defineProps({
  entidade: { type: String, required: true },
  idEntidade: { type: Number, required: true },
  processos: { type: Array, default: () => [] },
  podeSolicitar: { type: Boolean, default: false },
  podeAnalisar: { type: Boolean, default: false },
  modo: { type: String, default: 'parent-driven' },
  solicitacaoLiberada: { type: Object, default: null },
  moduloRegraAcesso: { type: String, default: 'APROVACAO_VENDAS' },
  semFrame: { type: Boolean, default: false }
})

const emit = defineEmits([
  'solicitacao-preenchida',
  'solicitacao-disparada',
  'analise-completa',
  'show-notification'
])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const listas = reactive({})

// --- Estado ---
const historico = ref([])
const loading = ref(false)
const expanded = ref(true)
const modalAberto = ref(false)
const modalModo = ref('preenchimento')
const modalProcesso = ref(null)
const gruposAprovacao = ref([])
const loadingGrupos = ref(false)
const solicitacaoEmPreenchimento = ref(null)
const solicitacaoEmEdicao = ref(null)
const solicitacaoEmVisualizacao = ref(null)

// Converte o código de um campo Lst* para o texto amigável via Dicionário de Metadados.
// Nunca usa códigos de lista hard-coded: a origem é sempre a lista carregada.
const getTextoLista = (lista, id) => {
  if (!lista || id === null || id === undefined || id === '') return id || '-'
  const found = lista.find(l =>
    String(l.idListaClienteItem) === String(id) || String(l.idListaSistemaItem) === String(id)
  )
  return found ? found.textoLista : (id || '-')
}

const obterTextoProcesso = (lstProcesso) => getTextoLista(listas.LstProcessoAprovacao, lstProcesso)
const obterTextoStatus = (lstStatus) => getTextoLista(listas.LstStatusAprovacao, lstStatus)

// Classe visual derivada do TEXTO do status (não do código da lista)
const obterClasseStatus = (textoStatus) => {
  const texto = String(textoStatus || '').toUpperCase()
  if (texto.includes('REPROV') || texto.includes('CANCEL') || texto.includes('ENCERR')) return 'badge-ghost-danger'
  if (texto.includes('APROV') || texto.includes('CONCLU')) return 'badge-ghost-success'
  if (texto.includes('PENDEN')) return 'badge-ghost-warning'
  return 'badge-ghost-secondary'
}

const totalSolicitacoes = computed(() => historico.value.length)

// --- Carregar histórico ---
const carregarHistorico = async () => {
  if (!props.idEntidade) return
  loading.value = true
  try {
    const response = await api.get('/WorkflowAprovacaoAnalise', {
      params: {
        entidade: props.entidade,
        idEntidade: props.idEntidade,
        pageSize: 999
      }
    })
    // Solicitações "Inativa" não são requisições reais de aprovação (pedido sem
    // negociação de preço) e não devem aparecer no grid.
    // Ordenação do grid sempre por IdWorkflowAprovacaoAnalise (crescente)
    historico.value = (response.data || [])
      .filter(item => item.lstStatusAprovacao !== WORK_APR_ST.Inativa)
      .slice()
      .sort((a, b) => (a.idWorkflowAprovacaoAnalise || 0) - (b.idWorkflowAprovacaoAnalise || 0))
  } catch (error) {
    console.error('Erro ao carregar histórico de aprovações:', error)
    historico.value = []
  } finally {
    loading.value = false
  }
}

// --- Chave de permissão do processo selecionado ---
// Cada processo de aprovação tem sua própria permissão granular
// (ex: APROVACAO_VENDAS_COMERCIAL_APROVAR). A chave é declarada no item
// de `processos` (prop) e usada para buscar grupos e aprovadores.
const chavePermissaoAtual = computed(() => {
  const processo = props.processos.find(p => p.chave === modalProcesso.value)
  return processo?.chavePermissao || ''
})

// --- Carregar grupos de aprovação (padrão PermissoesConsulta) ---
const carregarGruposAprovacao = async (chave) => {
  if (!chave) {
    gruposAprovacao.value = []
    return
  }
  loadingGrupos.value = true
  try {
    let url = `/PermissoesConsulta/grupos?chave=${encodeURIComponent(chave)}`
    if (authStore.isAdminInterno) url += `&targetIdEmpresa=${authStore.empresaAtiva}`
    const response = await api.get(url)
    gruposAprovacao.value = Array.isArray(response.data) ? response.data : []
  } catch (error) {
    console.error('Erro ao carregar grupos de aprovação:', error)
    gruposAprovacao.value = []
  } finally {
    loadingGrupos.value = false
  }
}

// --- Liberar solicitação (Momento 1) ---
const liberarSolicitacao = (processo) => {
  const chave = typeof processo === 'string' ? processo : processo.chave
  // Label resolvido preferencialmente pelo Dicionário de Metadados (código -> texto)
  const labelFromLista = obterTextoProcesso(chave)
  const labelProp = props.processos.find(p => p.chave === chave)?.label
  const label = (labelFromLista && labelFromLista !== chave) ? labelFromLista : (labelProp || chave)
  solicitacaoEmPreenchimento.value = {
    processo: chave,
    label
  }
  modalProcesso.value = chave
  modalModo.value = 'preenchimento'
  modalAberto.value = true
  carregarGruposAprovacao(props.processos.find(p => p.chave === chave)?.chavePermissao || '')
}

// --- Preencher solicitação existente (status "Criada") ---
const preencherSolicitacao = (solicitacao) => {
  solicitacaoEmEdicao.value = solicitacao
  solicitacaoEmVisualizacao.value = null
  modalProcesso.value = solicitacao.lstProcessoAprovacao
  modalModo.value = 'preenchimento'
  modalAberto.value = true
  carregarGruposAprovacao(props.processos.find(p => p.chave === solicitacao.lstProcessoAprovacao)?.chavePermissao || '')
}

// --- Visualizar solicitação (qualquer status exceto Criada) ---
const visualizarSolicitacao = (solicitacao) => {
  solicitacaoEmVisualizacao.value = solicitacao
  solicitacaoEmEdicao.value = null
  modalProcesso.value = solicitacao.lstProcessoAprovacao
  modalModo.value = 'visualizacao'
  modalAberto.value = true
}

// --- Disparar solicitação (Momento 3) ---
const dispararSolicitacao = async () => {
  if (!solicitacaoEmPreenchimento.value) {
    emit('show-notification', { message: 'Nenhuma solicitação em preenchimento.', type: 'warning' })
    return
  }
  const preenchimento = solicitacaoEmPreenchimento.value
  try {
    const postPayload = {
      lstProcessoAprovacao: preenchimento.processo,
      resumoSolicitacao: preenchimento.resumo || '',
      idGrupoAcessoAprovador: preenchimento.idGrupoAcessoAprovador,
      idUsuarioAprovador: preenchimento.idUsuarioAprovador || null,
      opcoesAprovacaoJson: preenchimento.opcoesAprovacaoJson || null
    }
    // Adiciona FK lógica conforme a entidade
    if (props.entidade === 'PedidoVenda') {
      postPayload.idPedidoVenda = props.idEntidade
    } else if (props.entidade === 'PurchaseOrder') {
      postPayload.idPurchaseOrder = props.idEntidade
    }
    const response = await api.post('/WorkflowAprovacaoAnalise', postPayload)
    emit('solicitacao-disparada', {
      idWorkflowAprovacaoAnalise: response.data?.idWorkflowAprovacaoAnalise || null
    })
    emit('show-notification', { message: 'Solicitação de aprovação enviada com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao criar solicitação de aprovação.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
    return
  }
  solicitacaoEmPreenchimento.value = null
  await carregarHistorico()
}

// --- Abrir modal para nova solicitação (Modo 2 - user-initiated) ---
const abrirModalSolicitacao = () => {
  modalProcesso.value = null
  modalModo.value = 'preenchimento'
  modalAberto.value = true
  carregarGruposAprovacao('')
}

// --- Confirmar preenchimento (vindo do modal) ---
const onConfirmarPreenchimento = async (payload) => {
  // Preenchimento de uma solicitação existente (status "Criada")
  if (solicitacaoEmEdicao.value) {
    try {
      const putPayload = {
        resumoSolicitacao: payload.resumo,
        idGrupoAcessoAprovador: payload.idGrupoAcessoAprovador,
        idUsuarioAprovador: payload.idUsuarioAprovador || null,
        opcoesAprovacaoJson: payload.opcoesAprovacaoJson || null,
        xmin: solicitacaoEmEdicao.value.xmin
      }
      await api.put(`/WorkflowAprovacaoAnalise/${solicitacaoEmEdicao.value.idWorkflowAprovacaoAnalise}`, putPayload)
      emit('show-notification', { message: 'Solicitação preenchida com sucesso!', type: 'success' })
      modalAberto.value = false
      solicitacaoEmEdicao.value = null
      await carregarHistorico()
    } catch (error) {
      const data = error.response?.data
      let msg = 'Erro ao preencher a solicitação.'
      if (typeof data === 'string') msg = data
      else if (data?.message) msg = data.message
      else if (data?.title) msg = data.title
      else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
      emit('show-notification', { message: msg, type: 'error' })
    }
    return
  }

  if (props.modo === 'parent-driven') {
    // Modo 1: pai gerencia o fluxo
    emit('solicitacao-preenchida', payload)
    const labelFromLista = obterTextoProcesso(payload.processo)
    const labelProp = props.processos.find(p => p.chave === payload.processo)?.label
    solicitacaoEmPreenchimento.value = {
      processo: payload.processo,
      label: (labelFromLista && labelFromLista !== payload.processo) ? labelFromLista : (labelProp || payload.processo),
      resumo: payload.resumo,
      idGrupoAcessoAprovador: payload.idGrupoAcessoAprovador,
      idUsuarioAprovador: payload.idUsuarioAprovador || null,
      opcoesAprovacaoJson: payload.opcoesAprovacaoJson || null
    }
    modalAberto.value = false
  } else {
    // Modo 2: dispara direto
    try {
      const postPayload = {
        lstProcessoAprovacao: payload.processo,
        resumoSolicitacao: payload.resumo,
        idGrupoAcessoAprovador: payload.idGrupoAcessoAprovador,
        idUsuarioAprovador: payload.idUsuarioAprovador || null,
        opcoesAprovacaoJson: payload.opcoesAprovacaoJson || null
      }
      // Adiciona FK lógica conforme a entidade
      if (props.entidade === 'PedidoVenda') {
        postPayload.idPedidoVenda = props.idEntidade
      } else if (props.entidade === 'PurchaseOrder') {
        postPayload.idPurchaseOrder = props.idEntidade
      }
      await api.post('/WorkflowAprovacaoAnalise', postPayload)
      emit('show-notification', { message: 'Solicitação de aprovação enviada com sucesso!', type: 'success' })
      modalAberto.value = false
      await carregarHistorico()
    } catch (error) {
      const data = error.response?.data
      let msg = 'Erro ao criar solicitação de aprovação.'
      if (typeof data === 'string') msg = data
      else if (data?.message) msg = data.message
      else if (data?.title) msg = data.title
      else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
      emit('show-notification', { message: msg, type: 'error' })
    }
  }
}

// Limpa a solicitação em edição/visualização ao fechar o modal
watch(modalAberto, (aberto) => {
  if (!aberto) {
    solicitacaoEmEdicao.value = null
    solicitacaoEmVisualizacao.value = null
  }
})

// --- Watch para idEntidade ---
watch(() => props.idEntidade, async (newId) => {
  if (newId) {
    await carregarHistorico()
  } else {
    historico.value = []
  }
})

// --- Watch para solicitacaoLiberada (pai libera) ---
watch(() => props.solicitacaoLiberada, (nova) => {
  if (nova) {
    liberarSolicitacao(nova)
  }
}, { immediate: false })

// --- Carregar listas do Dicionário de Metadados para a tabela do workflow ---
const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['WorkflowAprovacaoAnalise'])
    Object.assign(listas, result)
  } catch (e) {
    console.error('Erro ao carregar listas do workflow de aprovação:', e)
  }
}

onMounted(async () => {
  await carregarListas()
  if (props.idEntidade) {
    await carregarHistorico()
  }
})

// --- Expor métodos ---
defineExpose({
  liberarSolicitacao,
  dispararSolicitacao,
  recarregar: carregarHistorico
})
</script>

<template>
  <!-- ============================================================ -->
  <!-- MODO COM FRAME (padrão): card com header accordion           -->
  <!-- ============================================================ -->
  <template v-if="!semFrame">
    <div class="card mb-3 border-info-subtle shadow-sm">
      <!-- Header -->
      <div
        class="card-header bg-info-subtle py-2 cursor-pointer d-flex justify-content-between align-items-center"
        @click="expanded = !expanded"
      >
        <h6 class="mb-0 text-info-emphasis fw-bold d-flex align-items-center gap-2">
          <i class="bi bi-check2-circle"></i>
          Aprovações
          <span
            v-if="totalSolicitacoes > 0"
            class="badge bg-info bg-opacity-25 text-info-emphasis border border-info-subtle fs-6"
          >
            {{ totalSolicitacoes }}
          </span>
        </h6>
        <div class="d-flex align-items-center gap-2">
          <!-- Botão Solicitar (Modo 2) -->
          <button
            v-if="modo === 'user-initiated' && podeSolicitar"
            type="button"
            class="btn btn-sm btn-info text-white"
            @click.stop="abrirModalSolicitacao"
          >
            <i class="bi bi-plus-lg me-1"></i> Solicitar aprovação
          </button>
          <i class="bi fs-5" :class="expanded ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
      </div>

      <!-- Body -->
      <div v-show="expanded" class="card-body p-0 border-top border-info-subtle">
        <!-- Alerta de solicitação liberada (Momento 1) -->
        <div
          v-if="solicitacaoEmPreenchimento"
          class="alert alert-info border-0 rounded-0 m-0 d-flex align-items-center cursor-pointer"
          @click="liberarSolicitacao(solicitacaoEmPreenchimento)"
        >
          <i class="bi bi-clipboard-check fs-4 me-3 text-info"></i>
          <div class="flex-grow-1">
            <strong>📋 Solicitação de {{ solicitacaoEmPreenchimento.label || solicitacaoEmPreenchimento.processo }} liberada para preenchimento</strong>
            <div class="small text-info-emphasis mt-1">Clique aqui para preencher os detalhes da solicitação.</div>
          </div>
          <i class="bi bi-arrow-right-circle fs-4 text-info"></i>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-info me-2"></div>
          Carregando aprovações...
        </div>

        <!-- Grid de histórico -->
        <div v-else-if="historico.length > 0" class="table-responsive">
          <table class="table table-hover admin-table mb-0">
            <thead>
              <tr>
                <th style="width: 60px;">#</th>
                <th>Processo</th>
                <th>Status</th>
                <th>Solicitante</th>
                <th>Enviado</th>
                <th>Definição</th>
                <th>Aprovador</th>
                <th>Parecer</th>
                <th v-if="podeSolicitar" class="text-end" style="width: 100px;">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in historico" :key="item.idWorkflowAprovacaoAnalise || idx">
                <td><code class="small">#{{ item.idWorkflowAprovacaoAnalise }}</code></td>
                <td>
                  <span class="small">{{ item.nomeProcesso || obterTextoProcesso(item.lstProcessoAprovacao) }}</span>
                </td>
                <td>
                  <span class="badge" :class="obterClasseStatus(item.nomeStatus || obterTextoStatus(item.lstStatusAprovacao))">
                    {{ item.nomeStatus || obterTextoStatus(item.lstStatusAprovacao) }}
                  </span>
                </td>
                <td>
                  <span class="small">{{ item.nomeSolicitante || '-' }}</span>
                </td>
                <td>
                  <span class="small">{{ item.tsEnviadoAprovacao ? formatarDataBR(item.tsEnviadoAprovacao) : '-' }}</span>
                </td>
                <td>
                  <span class="small">{{ item.tsDefinicaoAnalise ? formatarDataBR(item.tsDefinicaoAnalise) : '-' }}</span>
                </td>
                <td>
                  <span class="small">{{ item.nomeAprovador || '-' }}</span>
                </td>
                <td>
                  <span class="small">
                    {{ item.parecerAnalise || '-' }}
                  </span>
                </td>
                <td v-if="podeSolicitar" class="text-end">
                  <!-- Status Criada: botão de edição (preencher solicitação) -->
                  <button
                    v-if="item.lstStatusAprovacao === WORK_APR_ST.Criada"
                    type="button"
                    class="btn btn-sm btn-outline-info"
                    title="Preencher solicitação"
                    @click.stop="preencherSolicitacao(item)"
                  >
                    <i class="bi bi-pencil-square"></i>
                  </button>
                  <!-- Demais status: botão de visualização (viewer) -->
                  <button
                    v-else
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    title="Visualizar solicitação"
                    @click.stop="visualizarSolicitacao(item)"
                  >
                    <i class="bi bi-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center py-4 text-body-secondary">
          <i class="bi bi-inbox fs-3 d-block mb-2"></i>
          <span>Nenhuma solicitação de aprovação encontrada.</span>
        </div>
      </div>

      <!-- Modal de preenchimento/visualização -->
      <WorkflowAprovacaoModal
        v-model="modalAberto"
        :modo="modalModo"
        :fluxo="modo"
        :processo="modalProcesso"
        :solicitacao-edicao="solicitacaoEmEdicao"
        :solicitacao="solicitacaoEmVisualizacao"
        :grupos-aprovacao="gruposAprovacao"
        :chave-permissao="chavePermissaoAtual"
        :loading="loadingGrupos"
        @confirmar-preenchimento="onConfirmarPreenchimento"
        @solicitar-analise="onConfirmarPreenchimento"
      />
    </div>
  </template>

  <!-- ============================================================ -->
  <!-- MODO SEM FRAME: apenas o grid, sem card nem accordion        -->
  <!-- ============================================================ -->
  <template v-else>
    <!-- Alerta de solicitação liberada (Momento 1) -->
    <div
      v-if="solicitacaoEmPreenchimento"
      class="alert alert-info border-0 rounded-3 mb-3 d-flex align-items-center cursor-pointer"
      @click="liberarSolicitacao(solicitacaoEmPreenchimento)"
    >
      <i class="bi bi-clipboard-check fs-4 me-3 text-info"></i>
      <div class="flex-grow-1">
        <strong>📋 Solicitação de {{ solicitacaoEmPreenchimento.label || solicitacaoEmPreenchimento.processo }} liberada para preenchimento</strong>
        <div class="small text-info-emphasis mt-1">Clique aqui para preencher os detalhes da solicitação.</div>
      </div>
      <i class="bi bi-arrow-right-circle fs-4 text-info"></i>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-info me-2"></div>
      Carregando aprovações...
    </div>

    <!-- Grid de histórico -->
    <div v-else-if="historico.length > 0" class="table-responsive">
      <table class="table table-hover admin-table mb-0">
        <thead>
          <tr>
            <th style="width: 60px;">#</th>
            <th>Processo</th>
            <th>Status</th>
            <th>Solicitante</th>
            <th>Enviado</th>
            <th>Definição</th>
            <th>Aprovador</th>
            <th>Parecer</th>
            <th v-if="podeSolicitar" class="text-end" style="width: 100px;">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in historico" :key="item.idWorkflowAprovacaoAnalise || idx">
            <td><code class="small">#{{ item.idWorkflowAprovacaoAnalise }}</code></td>
            <td>
              <span class="small">{{ item.nomeProcesso || obterTextoProcesso(item.lstProcessoAprovacao) }}</span>
            </td>
            <td>
              <span class="badge" :class="obterClasseStatus(item.nomeStatus || obterTextoStatus(item.lstStatusAprovacao))">
                {{ item.nomeStatus || obterTextoStatus(item.lstStatusAprovacao) }}
              </span>
            </td>
            <td>
              <span class="small">{{ item.nomeSolicitante || '-' }}</span>
            </td>
            <td>
              <span class="small">{{ item.tsEnviadoAprovacao ? formatarDataBR(item.tsEnviadoAprovacao) : '-' }}</span>
            </td>
            <td>
              <span class="small">{{ item.tsDefinicaoAnalise ? formatarDataBR(item.tsDefinicaoAnalise) : '-' }}</span>
            </td>
            <td>
              <span class="small">{{ item.nomeAprovador || '-' }}</span>
            </td>
            <td>
              <span class="small">
                {{ item.parecerAnalise || '-' }}
              </span>
            </td>
            <td v-if="podeSolicitar" class="text-end">
              <!-- Status Criada: botão de edição (preencher solicitação) -->
              <button
                v-if="item.lstStatusAprovacao === WORK_APR_ST.Criada"
                type="button"
                class="btn btn-sm btn-outline-info"
                title="Preencher solicitação"
                @click.stop="preencherSolicitacao(item)"
              >
                <i class="bi bi-pencil-square"></i>
              </button>
              <!-- Demais status: botão de visualização (viewer) -->
              <button
                v-else
                type="button"
                class="btn btn-sm btn-outline-secondary"
                title="Visualizar solicitação"
                @click.stop="visualizarSolicitacao(item)"
              >
                <i class="bi bi-eye"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div v-else class="text-center py-4 text-body-secondary">
      <i class="bi bi-inbox fs-3 d-block mb-2"></i>
      <span>Nenhuma solicitação de aprovação encontrada.</span>
    </div>

    <!-- Modal de preenchimento/visualização -->
    <WorkflowAprovacaoModal
      v-model="modalAberto"
      :modo="modalModo"
      :fluxo="modo"
      :processo="modalProcesso"
      :solicitacao-edicao="solicitacaoEmEdicao"
      :solicitacao="solicitacaoEmVisualizacao"
      :grupos-aprovacao="gruposAprovacao"
      :chave-permissao="chavePermissaoAtual"
      :loading="loadingGrupos"
      @confirmar-preenchimento="onConfirmarPreenchimento"
      @solicitar-analise="onConfirmarPreenchimento"
    />
  </template>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
